<template>
  <div class="curation-table">
    <UiTable :data="curations" :columns="columnDefs" class="ui-sortable-search-result-table">
      <template #header="{ col, colIndex, label }">
        {{ label }}
      </template>
      <template #column-image="{ row }">
        <a class="card-image">
          <UiImageSelection :image="row.image" :w="48" :h="27" type="curation" image-usage="thumbnail" /></a
        >
      </template>
      <template #column-title="{ row }">
        <BaseLink
          to="/mygallery/view"
          @click="selectGallery(row)"
        >{{ ls(row.title) }}</BaseLink>
      </template>
      <template #column-edit="{ row }">
        <BaseLink
          to="/mygallery/edit"
          @click="selectGallery(row)"
        >
          <BaseIcon name="mdi/pencil" /></BaseLink>
        <BaseDropdown>
          <template #trigger="">
            <button class="jps-icon-button">
              <BaseIcon name="mdi/dots-horizontal" />
            </button>
          </template>
          <BaseDropdownItem @click="deleteGallery(row)">{{
            lss('削除', 'Delete')
          }}</BaseDropdownItem>
        </BaseDropdown>
      </template>
    </UiTable>
    <div class="add-gallery-row">
      <div class="add-gallery-options">
        <BaseRadio v-model="_addGalleryMode" native-value="new">{{ lss('新しいページを作成', 'Create a new page') }}</BaseRadio>
        <BaseRadio v-model="_addGalleryMode" native-value="template">{{ lss('テンプレートから作成', 'Create from template') }}</BaseRadio>
      </div>
      <button
        class="jps-button jps-button-with-left-icon add-gallery-button"
        @click="addGallery()"
      >
        <BaseIcon name="mdi/plus-circle" />
        {{ lss('ページを追加', 'Add page') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ColumnDef } from '~/components/ui/UiTable.vue'

const { ls, lss, ldate } = useI18n()

interface Props {
  curations: Curation[]
  addGalleryMode: 'new' | 'template'
}
const props = withDefaults(defineProps<Props>(), {})
const emit = defineEmits(['select-gallery', 'delete-gallery', 'add-gallery', 'change-add-gallery-mode'])

const _addGalleryMode = computed({
  get: () => props.addGalleryMode,
  set: (value: string) => {
    emit('change-add-gallery-mode', value)
  },
})

const columnDefs = [
  {
    key: 'image',
  },
  {
    key: 'title',
    label: () => ls({ ja: 'タイトル', en: 'Title' }),
    path: 'title',
    sort: 'title.yomi.sort',
  },
  {
    label: () => ls({ ja: '概要', en: 'Overview' }),
    path: 'summary',
    display: (val: LangString) => ls(val),
  },
  {
    label: () => ls({ ja: '最終更新', en: 'Updated' }),
    path: ['edit.date'],
    sort: "edit.date",
    display: (val: number) => ldate(val),
  },
  {
    key: 'edit',
  },
] as ColumnDef[]

const selectGallery = (item: Curation) => {
  emit('select-gallery', item)
}

const deleteGallery = (item: Curation) => {
  emit('delete-gallery', item.id)
}

const addGallery = () => {
  emit('add-gallery')
}
</script>

<style lang="scss">
.search-result-table {
  overflow-x: auto;

  .jps-srt-edit {
    display: flex;
    gap: 1rem;
  }

  .jps-srt-title {
    width: 30%;
  }
  .jps-srt-provider {
    width: 10%;
  }
  .jps-srt-db {
    width: 10%;
  }
  .jps-srt-fav {
    width: 1rem;
  }
}
</style>
<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

// UiTableはBaseTableを使ってtableを生成
.curation-table {
  :deep(table) {
    width: 100%;
    margin-bottom: 2rem;
  }
  :deep(thead tr th) {
    font-size: .75rem;
    font-weight: normal;
    text-align: left;
  }
  :deep(.ui-sortable-search-result-table tr:nth-child(even)) {
    background-color: $white;
  }
  a.card-image {
    :deep(img) {
      // UiImageSelectionにはw, hのpropsはないので上書き
      width: 48px;
      height: 27px;
    }
  }
}

.add-gallery-row {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  width: 100%;

  .add-gallery-options {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    margin-left: -.5rem;
  }

  .add-gallery-button {
    margin: 0;
    width: 14rem;
    color: $jps-gray-900;
    justify-content: center;
    text-align: center;
    &:not(:disabled):hover {
      color: $jps-gray-900;
    }
  }
}
</style>
